import React, { useState, useEffect, useCallback } from 'react';
import { fetchManuLeads, approveManuLead, rejectManuLead } from '../api';
import './Dashboard.css';
import './ManuLeads.css';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'invited', label: 'Invited' },
  { value: 'registered', label: 'Registered' },
  { value: 'rejected', label: 'Rejected' },
  { value: '', label: 'All' }
];

const ManuLeads = ({ session, onLogout, onPendingCountChange }) => {
  const [leads, setLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [confirm, setConfirm] = useState(null); // { lead, action }
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const accessToken = session?.access_token;

  const loadLeads = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await fetchManuLeads(accessToken, statusFilter);
      setLeads(data);
    } catch (err) {
      if (err.message === 'FORBIDDEN') { onLogout(); return; }
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, statusFilter, onLogout]);

  // Pending count for badge — fetch separately so it stays accurate across tabs
  const refreshPendingCount = useCallback(async () => {
    try {
      const data = await fetchManuLeads(accessToken, 'pending');
      onPendingCountChange?.(data.length);
    } catch (_) { /* ignore */ }
  }, [accessToken, onPendingCountChange]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { refreshPendingCount(); }, [refreshPendingCount]);

  const handleConfirm = async () => {
    if (!confirm) return;
    setActionLoading(true);
    try {
      if (confirm.action === 'approve') {
        await approveManuLead(accessToken, confirm.lead.id);
        setSuccessMsg(`Invite sent to ${confirm.lead.email}`);
      } else {
        await rejectManuLead(accessToken, confirm.lead.id);
        setSuccessMsg(`Rejection email sent to ${confirm.lead.email}`);
      }
      setConfirm(null);
      setTimeout(() => setSuccessMsg(''), 4000);
      loadLeads();
      refreshPendingCount();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusPill = (status) => {
    switch (status) {
      case 'invited': return <span className="admin-pill admin-pill-sent">Invited</span>;
      case 'registered': return <span className="admin-pill admin-pill-completed">Registered</span>;
      case 'rejected': return <span className="admin-pill admin-pill-failed">Rejected</span>;
      default: return <span className="admin-pill admin-pill-pending">Pending</span>;
    }
  };

  const pendingCount = statusFilter === 'pending'
    ? leads.length
    : leads.filter(l => l.status === 'pending').length;

  return (
    <>
      {successMsg && <div className="admin-success-banner">{successMsg}</div>}

      <div className="lead-summary">
        <div className="admin-summary-card lead-summary-card">
          <span className="admin-summary-label">Showing</span>
          <span className="admin-summary-value">{leads.length}</span>
          <span className="lead-summary-context">
            {statusFilter ? `${STATUS_TABS.find(tab => tab.value === statusFilter)?.label} applications` : 'All applications'}
          </span>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-table-wrapper leads-table-wrapper">
        <div className="admin-table-header-bar">
          <h2>Manufacturer Applications</h2>
          <div className="admin-table-header-right">
            <button className="admin-refresh-btn" onClick={loadLeads} disabled={isLoading}>Refresh</button>
            <span className="admin-table-count">{leads.length} items</span>
          </div>
        </div>

        <div className="admin-tabs">
          {STATUS_TABS.map(tab => (
            <button
              key={tab.value}
              className={`admin-tab ${statusFilter === tab.value ? 'admin-tab-active' : ''}`}
              onClick={() => setStatusFilter(tab.value)}
            >
              {tab.label}
              {tab.value === 'pending' && pendingCount > 0 && statusFilter !== 'pending' && (
                <span className="admin-tab-badge">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="admin-loading">Loading applications...</div>
        ) : leads.length === 0 ? (
          <div className="admin-empty">
            <h3>No {statusFilter || ''} applications</h3>
            <p>{statusFilter === 'pending' ? 'All caught up! No pending applications to review.' : 'Applications will appear here.'}</p>
          </div>
        ) : (
          <>
            <div className="admin-table-head leads-head">
              <span className="admin-col">Applicant</span>
              <span className="admin-col">Company</span>
              <span className="admin-col">Email</span>
              <span className="admin-col">Country</span>
              <span className="admin-col">Website / Social</span>
              <span className="admin-col">Submitted</span>
              <span className="admin-col">Status</span>
              <span className="admin-col">Action</span>
            </div>

            {leads.map((lead) => (
              <div key={lead.id} className={`admin-table-row leads-row ${lead.status === 'pending' ? 'admin-row-pending' : ''}`}>
                <span className="admin-col lead-applicant-col">
                  <span className="admin-manu-name">{lead.first_name} {lead.last_name}</span>
                </span>
                <span className="admin-col lead-company-col">
                  <span className="admin-manu-name">{lead.company_name || '—'}</span>
                </span>
                <span className="admin-col lead-email-col"><span className="lead-cell-text">{lead.email}</span></span>
                <span className="admin-col lead-country-col"><span className="lead-cell-text">{lead.location || '—'}</span></span>
                <span className="admin-col lead-links-col">
                  {lead.website ? (
                    <a className="lead-link" href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`} target="_blank" rel="noreferrer">Website</a>
                  ) : <span className="lead-cell-text">—</span>}
                  {lead.social_media_link && (
                    <a className="lead-link" href={lead.social_media_link.startsWith('http') ? lead.social_media_link : `https://${lead.social_media_link}`} target="_blank" rel="noreferrer">Social</a>
                  )}
                </span>
                <span className="admin-col lead-date-col">{formatDate(lead.created_at)}</span>
                <span className="admin-col lead-status-col">{getStatusPill(lead.status)}</span>
                <span className="admin-col lead-action-col">
                  {lead.status === 'pending' ? (
                    <div className="lead-actions">
                      <button className="admin-record-btn lead-approve-btn" onClick={() => setConfirm({ lead, action: 'approve' })}>Approve</button>
                      <button className="lead-reject-btn" onClick={() => setConfirm({ lead, action: 'reject' })}>Reject</button>
                    </div>
                  ) : (
                    <span className="admin-ref-num">—</span>
                  )}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      {confirm && (
        <div className="lead-modal-overlay" onClick={() => !actionLoading && setConfirm(null)}>
          <div className="lead-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{confirm.action === 'approve' ? 'Approve application?' : 'Reject application?'}</h3>
            <p className="lead-modal-sub">
              {confirm.action === 'approve'
                ? `An invite email with a registration link will be sent to ${confirm.lead.email}.`
                : `${confirm.lead.first_name} will receive an email letting them know they can reapply later.`}
            </p>
            <div className="lead-modal-info">
              <div><strong>{confirm.lead.first_name} {confirm.lead.last_name}</strong></div>
              <div>{confirm.lead.company_name}</div>
              <div className="lead-cell-text">{confirm.lead.email}</div>
            </div>
            <div className="lead-modal-actions">
              <button className="lead-modal-cancel" onClick={() => setConfirm(null)} disabled={actionLoading}>Cancel</button>
              <button
                className={confirm.action === 'approve' ? 'lead-modal-approve' : 'lead-modal-reject'}
                onClick={handleConfirm}
                disabled={actionLoading}
              >
                {actionLoading ? 'Sending...' : confirm.action === 'approve' ? 'Approve & Send Invite' : 'Reject & Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ManuLeads;
